/**
 * URL validation utilities for secure URL handling
 *
 * This module provides reusable functions for validating URLs, particularly
 * for Office viewer URLs and secure HTTPS validation.
 *
 * Security features:
 * - Strict hostname validation with normalization (lowercase, exact match)
 * - Protocol validation (HTTPS only for sensitive operations)
 * - Prevents bypasses like: trusted.com.evil, TRUSTED.COM, user@trusted.com@evil
 * - Safe URL parsing with error handling
 */

/**
 * Validates if a URL is a valid Office viewer URL
 *
 * Requirements:
 * - Must use HTTPS protocol
 * - Hostname must be exactly "view.officeapps.live.com" (case-insensitive)
 * - Hostname must exist and not be empty
 *
 * @param url - The URL string to validate
 * @returns true if the URL is a valid Office viewer URL, false otherwise
 *
 * @example
 * ```ts
 * isValidOfficeViewerUrl("https://view.officeapps.live.com/op/embed.aspx?src=...")
 * // => true
 *
 * isValidOfficeViewerUrl("http://view.officeapps.live.com/op/embed.aspx?src=...")
 * // => false (HTTP not allowed)
 *
 * isValidOfficeViewerUrl("https://view.officeapps.live.com.evil.com/op/embed.aspx")
 * // => false (subdomain attack)
 * ```
 */
export function isValidOfficeViewerUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Strict hostname validation with normalization (lowercase, exact match)
    // Prevents bypasses like: trusted.com.evil, TRUSTED.COM, user@trusted.com@evil
    const normalizedHostname = parsedUrl.hostname.toLowerCase();

    return (
      normalizedHostname === "view.officeapps.live.com" &&
      parsedUrl.protocol === "https:" &&
      !!parsedUrl.hostname // Ensure hostname exists and is not empty
    );
  } catch {
    return false;
  }
}

/**
 * Validates if a URL has a valid HTTPS protocol and hostname
 *
 * Requirements:
 * - Must use HTTPS protocol
 * - Must have a non-empty hostname
 *
 * @param url - The URL string to validate (may be undefined)
 * @returns true if the URL is valid HTTPS with hostname, false otherwise
 *
 * @example
 * ```ts
 * isValidHttpsUrl("https://example.com/file.pdf")
 * // => true
 *
 * isValidHttpsUrl("http://example.com/file.pdf")
 * // => false (HTTP not allowed)
 *
 * isValidHttpsUrl("https://")
 * // => false (no hostname)
 * ```
 */
export function isValidHttpsUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const parsedUrl = new URL(url);

    // Only allow HTTPS protocol and validate it's not malformed
    return parsedUrl.protocol === "https:" && !!parsedUrl.hostname;
  } catch {
    return false;
  }
}

/**
 * Extracts and validates the src parameter from an Office viewer URL
 *
 * This function:
 * 1. Validates the Office viewer URL
 * 2. Extracts the "src" query parameter
 * 3. Validates the src URL is HTTPS with a hostname
 * 4. Returns the validated src URL or null if invalid
 *
 * @param officeViewerUrl - The Office viewer URL containing the src parameter
 * @returns The validated src URL or null if invalid/missing
 *
 * @example
 * ```ts
 * const officeUrl = "https://view.officeapps.live.com/op/embed.aspx?src=https://example.com/file.xlsx";
 * extractValidSrcFromOfficeUrl(officeUrl)
 * // => "https://example.com/file.xlsx"
 *
 * const invalidUrl = "https://view.officeapps.live.com/op/embed.aspx?src=http://example.com/file.xlsx";
 * extractValidSrcFromOfficeUrl(invalidUrl)
 * // => null (src must be HTTPS)
 * ```
 */
export function extractValidSrcFromOfficeUrl(officeViewerUrl: string): string | null {
  try {
    const url = new URL(officeViewerUrl);

    // Validate this is a legitimate Office viewer URL
    if (!isValidOfficeViewerUrl(officeViewerUrl)) {
      return null;
    }

    const src = url.searchParams.get("src");

    if (!src) {
      return null;
    }

    // Validate the src parameter URL to prevent open redirect
    if (isValidHttpsUrl(src)) {
      return src;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extracts a safe download URL from a file URL
 *
 * This function handles two scenarios:
 * 1. Office viewer URLs: Extracts and validates the src parameter
 * 2. Regular URLs: Returns the URL as-is
 *
 * @param fileUrl - The file URL (could be Office viewer or regular URL)
 * @returns The safe download URL, or undefined if parsing fails
 *
 * @example
 * ```ts
 * // Office viewer URL
 * const officeUrl = "https://view.officeapps.live.com/op/embed.aspx?src=https://example.com/file.xlsx";
 * extractSafeDownloadUrl(officeUrl)
 * // => "https://example.com/file.xlsx"
 *
 * // Regular URL
 * extractSafeDownloadUrl("https://example.com/document.pdf")
 * // => "https://example.com/document.pdf"
 *
 * // Invalid URL
 * extractSafeDownloadUrl("not a url")
 * // => undefined
 * ```
 */
export function extractSafeDownloadUrl(fileUrl: string | null | undefined): string | undefined {
  if (!fileUrl) {
    return undefined;
  }

  try {
    // If it's an Office viewer URL, extract the src parameter for the original file URL
    const validSrc = extractValidSrcFromOfficeUrl(fileUrl);

    if (validSrc) {
      return validSrc;
    }

    // For non-Office viewer URLs, validate they can be parsed
    const parsedUrl = new URL(fileUrl);

    // Only allow http: and https: protocols for download URLs
    // Reject dangerous protocols like javascript:, data:, blob:, etc.
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return undefined;
    }

    return fileUrl;
  } catch {
    // If parsing fails, return undefined for safety
    return undefined;
  }
}

/**
 * Whether a URL is safe to render as a link or iframe source, based on protocol
 * alone. Accepts only `http:` and `https:`, rejecting dangerous schemes such as
 * `javascript:`, `data:` and `blob:`.
 *
 * This is the single shared implementation for the checks that previously lived
 * as private copies in IframeDashboard, useIntegrationOAuth, DataHubGoogleDrive
 * and DataHubConfluence.
 *
 * ⚠️ A passing protocol says nothing about the origin. When a URL is used as a
 * `postMessage` target for sensitive data (auth tokens, user identity), a
 * protocol check is not sufficient — use {@link isUrlOnAllowedOrigin} with an
 * explicit allowlist instead.
 *
 * @param url - The URL string to validate (may be undefined)
 * @returns true if the URL parses and uses an http(s) scheme
 */
export function isSafeUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);

    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Whether a URL is safe to use as an iframe `src`. Unlike {@link isSafeUrl} this
 * accepts same-origin relative paths (e.g. `/brands/ngv/dashboard.html`) by
 * resolving against the current origin, while still rejecting dangerous schemes
 * such as `data:` and `javascript:`.
 *
 * It does NOT constrain the origin, so use it only for trusted configuration
 * values (a tenant's dashboard URL, which may legitimately point at an external
 * vendor such as Looker or PowerBI). User-controlled input must instead go
 * through {@link isUrlOnAllowedOrigin}.
 *
 * @param url - The URL string to validate (may be undefined)
 * @returns true if the URL resolves to an http(s) resource
 */
export function isSafeIframeUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);

    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Whether a URL is safe to render AND resolves to one of an explicit set of
 * allowed origins.
 *
 * Use this — not {@link isSafeUrl} — whenever a user-controlled URL becomes a
 * `postMessage` target for sensitive data. The dashboard iframe posts the real
 * bearer JWT and the user's identity to the origin derived from its URL, so a
 * protocol-only check would let a `?dashboardUrl=https://evil.example` override
 * redirect the token to an attacker-controlled origin (the trust check is
 * otherwise circular because the trusted origin is derived from the same
 * attacker URL).
 *
 * Relative URLs are resolved against the current origin, so a same-origin
 * override is permitted only when the current origin is itself in
 * `allowedOrigins`. Origins are compared after normalisation through the URL
 * parser, so casing, default ports and trailing paths cannot be used to smuggle
 * a disallowed origin past the check.
 *
 * @param url - The URL string to validate (may be undefined)
 * @param allowedOrigins - Origins the URL is permitted to resolve to
 * @returns true if the URL is http(s) and its origin is in `allowedOrigins`
 */
export function isUrlOnAllowedOrigin(
  url: string | undefined,
  allowedOrigins: readonly string[],
): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);

    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return false;
    }

    const normalised = new Set(
      allowedOrigins.map((allowed) => {
        try {
          return new URL(allowed, window.location.origin).origin;
        } catch {
          return allowed;
        }
      }),
    );

    return normalised.has(parsed.origin);
  } catch {
    return false;
  }
}
